import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { chatAPI } from '../../utils/api';
import { formatDate } from '../../utils/timeFormat';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  ArrowLeft, Send, Users, MessageSquare, Lock,
  MapPin, Calendar, Loader2, User
} from 'lucide-react';
import { formatDateTime12h } from '../../utils/timeFormat';
import { toast } from 'sonner';

const ChatPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRoomId = searchParams.get('room');

  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const dashboardPath = user.role === 'client' ? '/client/dashboard' : '/talent/dashboard';

  const fetchRooms = useCallback(async () => {
    try {
      const res = await chatAPI.getRooms();
      setRooms(res.data);
      if (initialRoomId && !activeRoom) {
        const room = res.data.find(r => r.id === initialRoomId);
        if (room) setActiveRoom(room);
      }
    } catch (e) {
      // Silently fail for polling
    }
  }, [initialRoomId, activeRoom]);

  const fetchMessages = useCallback(async (roomId) => {
    if (!roomId) return;
    try {
      const res = await chatAPI.getMessages(roomId);
      setMessages(res.data.messages || []);
    } catch (e) {
      // Silently fail for polling
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchRooms();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (activeRoom) {
      setLoadingMessages(true);
      fetchMessages(activeRoom.id).finally(() => setLoadingMessages(false));
      // Poll for new messages every 5s
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => {
        fetchMessages(activeRoom.id);
        fetchRooms();
      }, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeRoom?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !activeRoom || sendingMsg) return;
    setSendingMsg(true);
    try {
      const res = await chatAPI.sendMessage(activeRoom.id, { content: newMessage.trim() });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (e) {
      toast.error('Failed to send message');
    } finally {
      setSendingMsg(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const selectRoom = async (room) => {
    setActiveRoom(room);
    setMessages([]);
    try {
      await chatAPI.markRead(room.id);
    } catch (e) {}
  };

  const getRoomIcon = (type) => {
    if (type === 'group') return <Users className="w-4 h-4" />;
    if (type === 'private') return <Lock className="w-4 h-4" />;
    return <MessageSquare className="w-4 h-4" />;
  };

  const getRoomBadge = (type) => {
    const styles = {
      group: 'bg-cyan-100 text-cyan-800',
      dm: 'bg-blue-100 text-blue-800',
      private: 'bg-amber-100 text-amber-800',
    };
    return <Badge className={`text-[10px] ${styles[type] || 'bg-gray-100'}`}>{type === 'dm' ? 'Direct' : type}</Badge>;
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const now = new Date();
    const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    if (d.toDateString() === now.toDateString()) {
      return timeStr;
    }
    return formatDate(d) + ' ' + timeStr;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadgeColor = (role) => {
    if (role === 'client') return 'bg-blue-500';
    if (role === 'admin') return 'bg-purple-500';
    return 'bg-cyan-500';
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="chat-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm shrink-0">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(dashboardPath)} data-testid="chat-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <MessageSquare className="w-5 h-5 text-cyan-600" />
          <h1 className="text-lg font-semibold text-gray-900">Messages</h1>
          {activeRoom && (
            <div className="ml-4 flex items-center gap-2 text-sm text-gray-500">
              <span className="font-medium text-gray-700">{activeRoom.name}</span>
              {getRoomBadge(activeRoom.room_type)}
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden max-w-7xl w-full mx-auto">
        {/* Room List Sidebar */}
        <div className={`${activeRoom ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r border-gray-200 bg-white`} data-testid="room-list">
          <div className="p-3 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Conversations</p>
          </div>
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
              </div>
            ) : rooms.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No conversations yet</p>
                <p className="text-xs text-gray-400 mt-1">Chats are created automatically when talent is booked for gigs.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {rooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => selectRoom(room)}
                    className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                      activeRoom?.id === room.id ? 'bg-cyan-50 border-l-2 border-cyan-500' : ''
                    }`}
                    data-testid="chat-room-item"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          {getRoomIcon(room.room_type)}
                          <span className="text-sm font-medium text-gray-900 truncate">{room.name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                          <MapPin className="w-3 h-3 shrink-0" />
                          <span className="truncate">{room.venue_name} - {room.city}</span>
                        </div>
                        {room.last_message && (
                          <p className="text-xs text-gray-500 truncate">
                            {room.last_message.is_system ? (
                              <span className="italic">{room.last_message.content}</span>
                            ) : (
                              <><span className="font-medium">{room.last_message.sender_name}:</span> {room.last_message.content}</>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {room.unread_count > 0 && (
                          <Badge className="bg-cyan-500 text-white text-[10px] px-1.5 min-w-[20px] justify-center" data-testid="unread-badge">
                            {room.unread_count}
                          </Badge>
                        )}
                        {room.last_message?.created_at && (
                          <span className="text-[10px] text-gray-400">{formatTime(room.last_message.created_at)}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Message Area */}
        <div className={`${activeRoom ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-gray-50`}>
          {!activeRoom ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Select a conversation to start messaging</p>
              </div>
            </div>
          ) : (
            <>
              {/* Room header (mobile back) */}
              <div className="md:hidden bg-white border-b border-gray-200 p-3 flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setActiveRoom(null)}>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <p className="text-sm font-medium text-gray-900">{activeRoom.name}</p>
                  <div className="flex items-center gap-1">
                    {getRoomBadge(activeRoom.room_type)}
                    <span className="text-xs text-gray-400">{activeRoom.member_count} members</span>
                  </div>
                </div>
              </div>

              {/* Members bar */}
              <div className="bg-white border-b border-gray-100 px-4 py-2 hidden md:flex items-center gap-3">
                <span className="text-xs text-gray-400 shrink-0">Members:</span>
                <div className="flex items-center gap-2 overflow-x-auto">
                  {activeRoom.members?.map((m) => (
                    <div key={m.user_id} className="flex items-center gap-1 shrink-0">
                      <div className={`w-5 h-5 rounded-full ${getRoleBadgeColor(m.role)} text-white text-[9px] flex items-center justify-center`}>
                        {getInitials(m.display_name)}
                      </div>
                      <span className="text-xs text-gray-600">{m.display_name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 px-4 py-3">
                {loadingMessages ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">No messages yet. Start the conversation!</div>
                ) : (
                  <div className="space-y-3" data-testid="messages-container">
                    {messages.map((msg) => {
                      if (msg.is_system) {
                        return (
                          <div key={msg.id} className="flex justify-center" data-testid="system-message">
                            <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{msg.content}</span>
                          </div>
                        );
                      }
                      const isMine = msg.is_mine;
                      return (
                        <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid="chat-message">
                          <div className={`max-w-[70%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
                            {!isMine && (
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <div className={`w-5 h-5 rounded-full ${getRoleBadgeColor(msg.sender_role)} text-white text-[9px] flex items-center justify-center`}>
                                  {getInitials(msg.sender_name)}
                                </div>
                                <span className="text-xs font-medium text-gray-600">{msg.sender_name}</span>
                                <Badge variant="outline" className="text-[9px] px-1 py-0">{msg.sender_role}</Badge>
                              </div>
                            )}
                            <div className={`rounded-2xl px-3.5 py-2 text-sm ${
                              isMine
                                ? 'bg-cyan-500 text-white rounded-br-md'
                                : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
                            }`}>
                              {msg.content}
                            </div>
                            <span className={`text-[10px] mt-0.5 ${isMine ? 'text-gray-400' : 'text-gray-400'}`}>
                              {formatTime(msg.created_at)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="bg-white border-t border-gray-200 p-3">
                <div className="flex items-center gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    className="flex-1 bg-gray-50 border-gray-200"
                    disabled={sendingMsg}
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!newMessage.trim() || sendingMsg}
                    className="bg-cyan-500 hover:bg-cyan-600 shrink-0"
                    size="sm"
                    data-testid="send-message-btn"
                  >
                    {sendingMsg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
